// Advanced Search Engine with Fuzzy Matching
// Handles natural language queries, typos, and intelligent ranking

export class HelpSearchEngine {
  constructor(content) {
    this.content = content;
    this.index = this.buildIndex(content);
  }

  buildIndex(content) {
    // Build search index for fast lookups
    const index = {
      byKeyword: new Map(),
      byCategory: new Map(),
      all: content
    };

    content.forEach((item, idx) => {
      // Index by keywords
      item.keywords.forEach(keyword => {
        if (!index.byKeyword.has(keyword.toLowerCase())) {
          index.byKeyword.set(keyword.toLowerCase(), []);
        }
        index.byKeyword.get(keyword.toLowerCase()).push(idx);
      });

      // Index by category
      if (!index.byCategory.has(item.category)) {
        index.byCategory.set(item.category, []);
      }
      index.byCategory.get(item.category).push(idx);
    });

    return index;
  }

  search(query, options = {}) {
    if (!query || query.trim().length === 0) {
      return this.getDefaultResults();
    }

    const normalizedQuery = query.toLowerCase().trim();
    const results = [];
    const seen = new Set();

    // 1. Exact phrase matches (highest priority)
    this.content.forEach((item, idx) => {
      const score = this.calculateRelevanceScore(item, normalizedQuery, 'exact');
      if (score > 0) {
        results.push({ ...item, score, matchType: 'exact', index: idx });
        seen.add(idx);
      }
    });

    // 2. Fuzzy keyword matches
    this.content.forEach((item, idx) => {
      if (seen.has(idx)) return;
      const score = this.calculateRelevanceScore(item, normalizedQuery, 'fuzzy');
      if (score > 0) {
        results.push({ ...item, score, matchType: 'fuzzy', index: idx });
        seen.add(idx);
      }
    });

    // Sort by relevance score (highest first)
    results.sort((a, b) => b.score - a.score);

    // Limit results
    const maxResults = options.limit || 8;
    return results.slice(0, maxResults);
  }

  calculateRelevanceScore(item, query, matchType) {
    let score = 0;
    const words = query.split(/\s+/).filter(w => w.length > 0);

    if (matchType === 'exact') {
      // Exact matches in title (highest value)
      if (item.title.toLowerCase().includes(query)) {
        score += 100;
      }
      // Exact matches in question
      if (item.question?.toLowerCase().includes(query)) {
        score += 80;
      }
      // Exact matches in answer preview
      if (item.answerPreview?.toLowerCase().includes(query)) {
        score += 40;
      }
      // Exact matches in keywords
      if (item.keywords.some(k => k.toLowerCase().includes(query))) {
        score += 60;
      }
    } else {
      // Fuzzy matching - check individual words
      words.forEach(word => {
        // Title matches
        if (item.title.toLowerCase().includes(word)) {
          score += 30;
        }
        // Question matches
        if (item.question?.toLowerCase().includes(word)) {
          score += 25;
        }
        // Keyword matches
        item.keywords.forEach(keyword => {
          if (keyword.toLowerCase().includes(word)) {
            score += 20;
          }
          // Fuzzy match (Levenshtein-like)
          if (this.fuzzyMatch(keyword.toLowerCase(), word)) {
            score += 10;
          }
        });
        // Answer preview matches
        if (item.answerPreview?.toLowerCase().includes(word)) {
          score += 15;
        }
      });
    }

    // Boost popular items
    if (item.popular) {
      score += 20;
    }

    // Boost by category priority
    const categoryBoost = {
      'quick-start': 15,
      'troubleshooting': 10,
      'settings': 8,
      'features': 5
    };
    score += categoryBoost[item.category] || 0;

    return score;
  }

  fuzzyMatch(str1, str2) {
    // Simple fuzzy matching - checks if strings are similar
    if (str1 === str2) return true;
    if (Math.abs(str1.length - str2.length) > 3) return false;

    let matches = 0;
    const len = Math.min(str1.length, str2.length);

    for (let i = 0; i < len; i++) {
      if (str1[i] === str2[i]) matches++;
    }

    return matches / len > 0.7; // 70% character match
  }

  getDefaultResults() {
    // Return popular/recommended items when no query
    return this.content
      .filter(item => item.popular || item.featured)
      .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
      .slice(0, 6);
  }

  getSuggestions(query) {
    // Get search suggestions as user types
    if (!query || query.length < 2) {
      return this.getPopularSearches();
    }

    const results = this.search(query, { limit: 5 });
    return results.map(r => ({
      text: r.question || r.title,
      category: r.category,
      id: r.id
    }));
  }

  getPopularSearches() {
    return [
      { text: "How do I add a phone number?", category: "quick-start" },
      { text: "What is A2P registration?", category: "settings" },
      { text: "Why aren't messages sending?", category: "troubleshooting" },
      { text: "How do I set up a campaign?", category: "quick-start" },
      { text: "What is a hot lead?", category: "features" },
      { text: "How do I add team members?", category: "settings" }
    ];
  }

  getRelatedContent(itemId) {
    const item = this.content.find(i => i.id === itemId);
    if (!item) return [];

    // Find related content by category and keywords
    return this.content
      .filter(i => i.id !== itemId)
      .map(i => {
        let relevance = 0;
        // Same category
        if (i.category === item.category) relevance += 30;
        // Shared keywords
        const sharedKeywords = i.keywords.filter(k =>
          item.keywords.includes(k)
        );
        relevance += sharedKeywords.length * 10;
        return { ...i, relevance };
      })
      .filter(i => i.relevance > 0)
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 3);
  }
}

export default HelpSearchEngine;
